/**
 * Typography system for Turn Connect applications.
 * Consistent text styles across web and mobile platforms.
 */

export const typography = {
  // Font families
  fontFamily: {
    primary: 'Archivo, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    mono: 'SFMono-Regular, "SF Mono", Monaco, Inconsolata, "Roboto Mono", "Droid Sans Mono", "Liberation Mono", Menlo, Courier, monospace',
    display: 'Archivo, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },

  // Font sizes - Responsive scale
  fontSize: {
    xs: 11, // Small labels, captions
    sm: 12, // Form labels, secondary text
    md: 13, // Body text, buttons
    lg: 15, // Large body text
    xl: 17, // Subheadings
    '2xl': 19, // Small headings
    '3xl': 22, // Medium headings
    '4xl': 26, // Large headings
    '5xl': 30, // Extra large headings
    '6xl': 34, // Display text
  },

  // Font weights
  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },

  // Line heights
  lineHeight: {
    tight: 1.1, // Headlines
    normal: 1.4, // Body text
    relaxed: 1.6, // Large text blocks
    loose: 1.8, // Reading content
  },

  // Letter spacing
  letterSpacing: {
    tighter: '-0.02em',
    tight: '-0.01em',
    normal: '0em',
    wide: '0.01em',
    wider: '0.02em',
    widest: '0.1em', // Uppercase labels
  },

  // Text styles - Pre-defined combinations
  styles: {
    // Headings
    h1: {
      fontSize: 32,
      fontWeight: 700,
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: 28,
      fontWeight: 700,
      lineHeight: 1.1,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: 24,
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: '0em',
    },
    h4: {
      fontSize: 20,
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '0em',
    },
    h5: {
      fontSize: 18,
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '0em',
    },
    h6: {
      fontSize: 16,
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '0em',
    },

    // Body text
    body: {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: 1.4,
      letterSpacing: '0em',
    },
    bodyLarge: {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: 1.4,
      letterSpacing: '0em',
    },
    bodySmall: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: 1.4,
      letterSpacing: '0em',
    },

    // Labels and captions
    label: {
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '0.01em',
    },
    labelLarge: {
      fontSize: 14,
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '0em',
    },
    caption: {
      fontSize: 11,
      fontWeight: 400,
      lineHeight: 1.3,
      letterSpacing: '0.01em',
    },

    // Button text
    button: {
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1,
      letterSpacing: '0em',
    },
    buttonSmall: {
      fontSize: 12,
      fontWeight: 600,
      lineHeight: 1,
      letterSpacing: '0em',
    },
    buttonLarge: {
      fontSize: 16,
      fontWeight: 600,
      lineHeight: 1,
      letterSpacing: '0em',
    },

    // Special text
    code: {
      fontFamily: 'SFMono-Regular, "SF Mono", Monaco, Inconsolata, "Roboto Mono", monospace',
      fontSize: 12,
      fontWeight: 400,
      lineHeight: 1.4,
      letterSpacing: '0em',
    },
    overline: {
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '0.1em',
      textTransform: 'uppercase' as const,
    },
  },
};

export type Typography = typeof typography;
